/**
 * YD Video Scrub Section
 * 视频自动播放 + 标题随视频时间切换
 *
 * - 视频: 25fps, 271 帧, 时长 ≈ 10.84s, loop 循环
 * - section 进入视口即播放，离开视口暂停（节省资源）
 * - 标题阈值按原 vh 比例换算到视频时间 (vh / scrubVh × duration)
 * - 视频循环回零时重置 Line Reveal 状态，循环切换文字
 */
(function() {
  'use strict';

  /**
   * 视频自动播放 + 标题切换控制器
   */
  class VideoAutoPlayAnimator {
    constructor(config) {
      this.config = config;
      this.scrubVh = config.scrubVh || 600;
      this.fps = config.fps || 25;
      this.totalFrames = config.totalFrames || 271;
      this.duration = this.totalFrames / this.fps;
      // 后台可配播放速度；clamp 到浏览器普遍稳定的范围
      const rate = parseFloat(config.playbackRate);
      this.playbackRate = isFinite(rate) && rate > 0 ? Math.min(Math.max(rate, 0.25), 4) : 1;

      this.section = null;
      this.wrapper = null;
      this.video = null;

      this.titleElements = {};
      this.splitTextInstances = {};
      this.revealedTitles = new Set();

      this.isPlaying = false;
      this.lastTime = 0;
      this.rafId = null;
    }

    /**
     * 把原本的 vh 阈值换算成视频时间 (s)
     */
    vhToTime(vh) {
      return (vh / this.scrubVh) * this.duration;
    }

    /**
     * 缓存标题元素，并把 vh 阈值预先换算成 time 阈值
     */
    cacheTitleElements() {
      const titles = this.config.titles || [];
      for (const titleConfig of titles) {
        if (!titleConfig.id) continue;
        const el = document.querySelector(`#${titleConfig.id}`);
        if (!el) continue;

        this.titleElements[titleConfig.id] = el;

        // 把 vh 阈值预换算为 time 阈值，避免每帧重复计算
        titleConfig._fadeIn = titleConfig.fadeIn
          ? {
              start: this.vhToTime(titleConfig.fadeIn.startVh),
              end: this.vhToTime(titleConfig.fadeIn.endVh)
            }
          : null;
        titleConfig._fadeOut = titleConfig.fadeOut
          ? {
              start: this.vhToTime(titleConfig.fadeOut.startVh),
              end: this.vhToTime(titleConfig.fadeOut.endVh)
            }
          : null;

        const hasNoFadeIn = !titleConfig.fadeIn;
        if (typeof gsap !== 'undefined') {
          gsap.set(el, { opacity: hasNoFadeIn ? 1 : 0 });
        }
      }
      this.initSplitText();
    }

    /**
     * 初始化 SplitText - 把每行内容包到 .yd-line-inner 里做 Line Reveal
     */
    initSplitText() {
      if (typeof SplitText === 'undefined') return;

      const titles = this.config.titles || [];
      for (const titleConfig of titles) {
        const container = this.titleElements[titleConfig.id];
        if (!container) continue;

        const textElements = container.querySelectorAll('h1, h2, p');
        const targets = textElements.length > 0 ? textElements : [container];

        const instances = [];
        targets.forEach(el => {
          const split = new SplitText(el, {
            type: 'lines',
            linesClass: 'yd-line'
          });

          split.lines.forEach(line => {
            const wrapper = document.createElement('div');
            wrapper.className = 'yd-line-inner';
            wrapper.innerHTML = line.innerHTML;
            line.innerHTML = '';
            line.appendChild(wrapper);
          });

          instances.push(split);
          gsap.set(el.querySelectorAll('.yd-line-inner'), { yPercent: 100 });
        });

        this.splitTextInstances[titleConfig.id] = instances;
      }
    }

    /**
     * 播放 Line Reveal
     */
    playLineReveal(titleId) {
      if (this.revealedTitles.has(titleId)) return;
      const container = this.titleElements[titleId];
      if (!container) return;

      const inners = container.querySelectorAll('.yd-line-inner');
      if (inners.length === 0) return;

      this.revealedTitles.add(titleId);
      gsap.fromTo(
        inners,
        { yPercent: 100 },
        { yPercent: 0, duration: 0.8, stagger: 0.12, ease: 'power3.out' }
      );
    }

    /**
     * 重置 Line Reveal 状态（隐藏时调用，让下一次淡入还能播）
     */
    resetLineReveal(titleId) {
      if (!this.revealedTitles.has(titleId)) return;
      const container = this.titleElements[titleId];
      if (!container) return;

      const inners = container.querySelectorAll('.yd-line-inner');
      if (inners.length === 0) return;

      this.revealedTitles.delete(titleId);
      gsap.set(inners, { yPercent: 100 });
    }

    /**
     * 视频循环回零时，重置所有已 reveal 的标题，让下一轮 loop 重新播放动画
     */
    resetAllRevealsForLoop() {
      Array.from(this.revealedTitles).forEach((id) => {
        const container = this.titleElements[id];
        if (!container) return;
        const inners = container.querySelectorAll('.yd-line-inner');
        if (inners.length > 0) {
          gsap.set(inners, { yPercent: 100 });
        }
      });
      this.revealedTitles.clear();
    }

    /**
     * 入场首次触发：让没有 fadeIn 配置的标题（intro）播一次 Line Reveal
     */
    triggerInitialLineReveal() {
      const titles = this.config.titles || [];
      for (const titleConfig of titles) {
        if (!titleConfig.fadeIn && this.titleElements[titleConfig.id]) {
          this.playLineReveal(titleConfig.id);
        }
      }
    }

    /**
     * 基于当前视频时间更新所有标题
     */
    updateTitles(currentTime) {
      const titles = this.config.titles || [];

      for (const titleConfig of titles) {
        const el = this.titleElements[titleConfig.id];
        if (!el) continue;

        let opacity = 1;

        if (titleConfig._fadeIn) {
          const { start, end } = titleConfig._fadeIn;
          if (currentTime < start) {
            opacity = 0;
          } else if (currentTime < end) {
            opacity = (currentTime - start) / (end - start);
          }
        }

        if (titleConfig._fadeOut && opacity > 0) {
          const { start, end } = titleConfig._fadeOut;
          if (currentTime >= end) {
            opacity = 0;
          } else if (currentTime > start) {
            opacity = 1 - (currentTime - start) / (end - start);
          }
        }

        if (opacity === 0 && this.revealedTitles.has(titleConfig.id)) {
          this.resetLineReveal(titleConfig.id);
        }

        if (opacity > 0 && !this.revealedTitles.has(titleConfig.id)) {
          if (titleConfig._fadeIn) {
            this.playLineReveal(titleConfig.id);
          } else if (opacity === 1) {
            this.playLineReveal(titleConfig.id);
          }
        }

        if (typeof gsap !== 'undefined') {
          gsap.set(el, { opacity: Math.max(0, Math.min(1, opacity)) });
        }
      }
    }

    /**
     * rAF 循环更新（比 timeupdate 高频，标题切换更平滑）
     */
    tick = () => {
      if (!this.video) return;

      const t = this.video.currentTime;

      // 检测 loop 回零：currentTime 从大值突然变小 → 视频回到开头
      // 阈值取 0.5s 避免误判
      if (this.lastTime > this.duration - 0.5 && t < 0.5) {
        this.resetAllRevealsForLoop();
      }
      this.lastTime = t;

      this.updateTitles(t);

      if (this.isPlaying) {
        this.rafId = requestAnimationFrame(this.tick);
      }
    };

    /**
     * 启动视频 + rAF 循环
     */
    startPlayback() {
      if (this.isPlaying) return;
      this.isPlaying = true;

      // 套用后台配置的播放速度（每次 play 都重新设置，因为部分浏览器 loop 时会重置）
      if (this.video.playbackRate !== this.playbackRate) {
        this.video.playbackRate = this.playbackRate;
      }

      const playPromise = this.video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }

      this.triggerInitialLineReveal();

      if (this.rafId == null) {
        this.rafId = requestAnimationFrame(this.tick);
      }
    }

    /**
     * 离开视口暂停（保留 currentTime，重新进入时继续播）
     */
    pausePlayback() {
      if (!this.isPlaying) return;
      this.isPlaying = false;

      if (this.video) {
        this.video.pause();
      }
      if (this.rafId != null) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
    }

    /**
     * 用 IntersectionObserver 监听 section 可见性
     */
    setupVisibilityObserver() {
      if (typeof IntersectionObserver === 'undefined') {
        // 老浏览器兜底：直接开始播放
        this.startPlayback();
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.startPlayback();
            } else {
              this.pausePlayback();
            }
          });
        },
        { threshold: 0.1 }
      );

      observer.observe(this.wrapper);
      this.observer = observer;
    }

    /**
     * 根据视口宽度 + 浏览器格式支持，选出唯一一个视频 URL 并插入 <source>
     * 只插入一个 source，避免重复下载
     */
    injectVideoSource() {
      const cfg = this.config.videoSources;
      if (!cfg) return;

      const breakpoint = cfg.mobileBreakpoint || 768;
      const isMobile = window.matchMedia(`(max-width: ${breakpoint - 0.02}px)`).matches;

      // 移动端字段为空时 fallback 到桌面
      const desktop = cfg.desktop || {};
      const mobile = cfg.mobile || {};
      const useMobile = isMobile && (mobile.mp4 || mobile.webm);

      const set = useMobile ? mobile : desktop;

      // 优先 webm（体积小），不支持则 mp4
      const canPlayWebm = !!this.video.canPlayType && this.video.canPlayType('video/webm').replace('no', '');
      let url = '';
      let mime = '';
      if (canPlayWebm && set.webm) {
        url = set.webm;
        mime = 'video/webm';
      } else if (set.mp4) {
        url = set.mp4;
        mime = 'video/mp4';
      } else if (set.webm) {
        url = set.webm;
        mime = 'video/webm';
      }

      if (!url) return;

      // 清空 video 内已有 source（防御性，应该本来就没有）
      while (this.video.firstChild) {
        this.video.removeChild(this.video.firstChild);
      }

      const source = document.createElement('source');
      source.src = url;
      source.type = mime;
      this.video.appendChild(source);

      // 触发浏览器加载新源
      this.video.load();
    }

    /**
     * 初始化
     */
    async initialize() {
      this.section = document.querySelector('#video-scrub-section');
      this.wrapper = document.querySelector('#video-scrub-wrapper');
      this.video = document.querySelector('#video-scrub-element');

      if (!this.section || !this.wrapper || !this.video) {
        return;
      }

      // 先注入 source（基于视口尺寸），再开始等加载
      this.injectVideoSource();

      this.cacheTitleElements();
      this.updateTitles(0);

      // 等视频可播放再启动 observer，避免 play() 在 metadata 之前调用报错
      const ready = () => {
        this.setupVisibilityObserver();
      };

      if (this.video.readyState >= 2) {
        ready();
      } else {
        const onCanPlay = () => {
          this.video.removeEventListener('canplay', onCanPlay);
          ready();
        };
        this.video.addEventListener('canplay', onCanPlay);

        // 兜底 5s 超时
        setTimeout(() => {
          this.video.removeEventListener('canplay', onCanPlay);
          if (!this.observer) ready();
        }, 5000);
      }
    }

    dispose() {
      this.pausePlayback();
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }
  }

  /**
   * 等待 gsap + SplitText 加载
   */
  function waitForDependencies() {
    return new Promise((resolve) => {
      let resolved = false;

      const check = setInterval(() => {
        if (typeof gsap !== 'undefined') {
          clearInterval(check);
          if (!resolved) {
            resolved = true;
            resolve(true);
          }
        }
      }, 100);

      setTimeout(() => {
        clearInterval(check);
        if (!resolved) {
          resolved = true;
          resolve(false);
        }
      }, 15000);
    });
  }

  /**
   * 等待 product-sequence 进入 INTRO_LOOP 状态（保留原有时序契约）
   */
  function waitForProductSequenceReady() {
    return new Promise((resolve) => {
      const stateMachine = window.ydProductSequenceStateMachine;
      if (
        stateMachine &&
        ['INTRO_LOOP', 'SCRUB', 'FINAL_LOOP'].includes(stateMachine.currentState)
      ) {
        resolve();
        return;
      }

      const handler = () => {
        window.removeEventListener('yd-product-sequence-ready', handler);
        resolve();
      };
      window.addEventListener('yd-product-sequence-ready', handler);

      setTimeout(() => {
        window.removeEventListener('yd-product-sequence-ready', handler);
        resolve();
      }, 8000);
    });
  }

  /**
   * 初始化入口
   */
  async function init() {
    if (window.ydVideoScrubAnimator) return;

    const section = document.querySelector('#video-scrub-section');
    if (!section) return;

    const configData = section.dataset.config;
    if (!configData) return;

    const config = JSON.parse(configData);

    const depsLoaded = await waitForDependencies();
    if (!depsLoaded) return;

    await waitForProductSequenceReady();

    const animator = new VideoAutoPlayAnimator(config);
    await animator.initialize();
    window.ydVideoScrubAnimator = animator;

    // 通知下游 section (product-sequence-2) 可以开始初始化
    window.dispatchEvent(
      new CustomEvent('yd-video-scrub-ready', {
        detail: { timestamp: Date.now() }
      })
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
